import { useState, useEffect, useRef } from "react";
import LibraryBookList from "./LibraryBookList";
import { track } from "../utils/analytics";
import "./HotBooks.css";

/**
 * 우리 지역 인기 책 — 시·도를 고르면 그 지역 도서관에서 많이 빌린 책.
 *
 * 인기 순위만 보이면 어느 지역이든 비슷하다(서울 상위 20권 중 18권이 전국과
 * 같았다). 그래서 '유독 많이 빌린 책' — 전국 순위보다 이 지역에서 훨씬 높은
 * 책 — 을 먼저 둔다. 자료는 새벽에 받아 둔다(scripts/fetch-region-books.mjs).
 *
 * 고른 지역은 이 기기에만 둔다. 사는 곳이라 서버에 남기지 않는다.
 *
 * 자료(91권의 소개글, 압축해도 37KB)는 처음 펼칠 때 받는다. 함께 묶으면
 * 이 버튼을 누르지 않는 사람도 앱을 열 때마다 그만큼 더 받는다.
 */
/* 버튼 이름에 쓸 지역 이름만 여기 둔다 — 자료를 받기 전에도 '제주 인기 책' 이라고 쓸 수 있게. */
const NAMES = { 11: "서울", 21: "부산", 22: "대구", 23: "인천", 24: "광주", 25: "대전", 26: "울산", 29: "세종",
  31: "경기", 32: "강원", 33: "충북", 34: "충남", 35: "전북", 36: "전남", 37: "경북", 38: "경남", 39: "제주" };
const KEY = "muroo-region";
const read = () => { try { return localStorage.getItem(KEY) || ""; } catch { return ""; } };

export default function RegionBooks({ uid = null, myBooks = [], canAdd = false }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState(read);
  const [DATA, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const rootRef = useRef(null);

  /* 새 소식의 '써보기' 가 열러 온다(HomeScreen 의 goNews). */
  useEffect(() => {
    const onOpen = () => {
      setOpen(true);
      setTimeout(() => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
    };
    window.addEventListener("muroo:open-regionbooks", onOpen);
    return () => window.removeEventListener("muroo:open-regionbooks", onOpen);
  }, []);

  useEffect(() => {
    if (!open || DATA) return;
    import("../data/regionBooks.json")
      .then((m) => setData(m.default ?? m))
      .catch(() => setFailed(true));
  }, [open, DATA]);

  const regions = DATA?.regions ?? {};
  const region = regions[code] && !regions[code].none ? regions[code] : null;

  const pick = (c) => {
    setCode(c);
    try { if (c) localStorage.setItem(KEY, c); else localStorage.removeItem(KEY); } catch { /* 이번만 */ }
    if (c) track("region_pick", { region: NAMES[c] });
  };
  const toBooks = (list) => (list ?? []).map((x) => ({ ...DATA.books[x.isbn], ...x })).filter((b) => b.title);
  const local = region ? toBooks(region.local) : [];
  const popular = region ? toBooks(region.popular) : [];
  const shared = { uid, myBooks, canAdd, event: "regionbooks_wish" };

  return (
    <div className="hb" ref={rootRef}>
      <button className="hb-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        📍 {NAMES[code] ? `${NAMES[code]} 인기 책` : "우리 지역 인기 책"}
        <span className="hb-caret" aria-hidden="true">{open ? "▴" : "▾"}</span>
      </button>

      {open && !DATA && (
        <div className="hb-panel">
          <div className="hb-head rb-empty">{failed ? "지금은 불러오지 못했어요. 잠시 뒤에 다시 열어 주세요." : "불러오는 중…"}</div>
        </div>
      )}
      {open && DATA && (
        <div className="hb-panel">
          <label className="rb-pick">
            <span>사는 곳</span>
            <select value={region ? code : ""} onChange={(e) => pick(e.target.value)}>
              <option value="">지역 고르기</option>
              {Object.entries(regions).map(([c, r]) => (
                <option key={c} value={c} disabled={r.none}>{r.name}{r.none ? " (자료 없음)" : ""}</option>
              ))}
            </select>
          </label>

          {!region ? (
            <div className="hb-head rb-empty">
              지역을 고르면 그 지역 도서관에서 요즘 많이 빌린 책을 보여드려요.
              고른 지역은 이 기기에만 저장돼요.
            </div>
          ) : (
            <>
              {/* 두 권 이하면 '유독' 이라 부르기 어렵다. 그때는 인기만 보인다. */}
              {local.length >= 2 && (
                <>
                  <div className="rb-title">✦ {region.name}에서 유독 많이 빌린 책</div>
                  <div className="hb-head">전국 순위보다 {region.name}에서 훨씬 많이 빌린 책이에요</div>
                  <LibraryBookList
                    books={local} {...shared}
                    side={(b) => (
                      <span className="hb-rise rb-side">
                        <b>{region.name} {b.rank}위</b>
                        <small>{b.nat ? `전국 ${b.nat}위` : "전국 순위 밖"}</small>
                      </span>
                    )}
                  />
                </>
              )}
              <div className="rb-title">{region.name} 인기 대출</div>
              <LibraryBookList
                books={popular} {...shared}
                side={(b) => (
                  <span className="hb-rise rb-side">
                    <b>{b.rank}위</b>
                    <small>{b.loans.toLocaleString("ko-KR")}회</small>
                  </span>
                )}
              />
              <div className="hb-foot">
                자료: 도서관 정보나루 · 20~59세 대출 · {DATA.from} ~ {DATA.to}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
